import { Link } from "@tanstack/react-router";
import { BrandMark } from "@/components/site/BrandMark";

const columns: { title: string; links: { to: string; label: string }[] }[] = [
  {
    title: "Platform & Research",
    links: [
      { to: "/platform", label: "Platform Overview" },
      { to: "/platform/structurelens", label: "StructureLens Scanner" },
      { to: "/weekly-outlook", label: "Weekly Gold Outlook" },
      { to: "/education", label: "Education Library" },
      { to: "/smart-money-concepts", label: "Smart Money Concepts" },
      { to: "/validation", label: "Validation Hub" },
    ],
  },
  {
    title: "Execution & Calculators",
    links: [
      { to: "/position-size-calculator", label: "Position Size Calculator" },
      { to: "/gold-pip-calculator", label: "Gold Pip Calculator" },
      { to: "/gold-margin-calculator", label: "Gold Margin Calculator" },
      { to: "/gold-daily-range-atr", label: "Gold Daily Range & ATR" },
      { to: "/xauusd-volatility-heatmap", label: "Gold Volatility Heatmap" },
      { to: "/gold-trading-sessions", label: "Gold Trading Hours Clock" },
      { to: "/gold-killzones-clock", label: "Gold Killzones Clock" },
      { to: "/prop-firm-gold", label: "Prop Firm Gold Trading Guide" },
      { to: "/best-prop-firms-for-gold", label: "Best Prop Firms for Gold" },
      { to: "/xauusd-trading", label: "How to Trade Gold (XAUUSD)" },
      { to: "/xauusd-analysis-today", label: "XAUUSD Analysis Today" },
      { to: "/gold-analysis", label: "XAUUSD Market Analysis" },
    ],
  },
  {
    title: "Macro & Strategy",
    links: [
      { to: "/gold-cot-report", label: "Gold COT Report (CFTC Positioning)" },
      { to: "/gold-inflation-cpi", label: "US CPI and Gold Inflation Tracker" },
      { to: "/gold-nfp-employment", label: "NFP and Gold Jobs Report Tracker" },
      { to: "/gold-dxy-correlation", label: "Gold and DXY Correlation" },
      { to: "/gold-seasonality", label: "Gold Seasonality Chart by Month" },
      { to: "/gold-retail-sentiment", label: "Gold Retail Sentiment (Long vs Short)" },
      { to: "/gold-options-sentiment", label: "Gold Options Sentiment (CBOE Data)" },
      { to: "/gold-real-yield", label: "US 10Y Real Yield and Gold (FRED Data)" },
      { to: "/gold-central-bank-reserves", label: "Central Bank Gold Reserves by Country" },
      { to: "/macrolens", label: "XAU MacroLens™ Gold Confluence Score" },
      { to: "/gold-trading-strategy", label: "Gold Trading Strategy Playbook" },
      { to: "/gold-scalping-strategy", label: "Gold Scalping Framework" },
      { to: "/what-is-xauusd", label: "What Is XAUUSD (Gold Symbol)" },
    ],
  },
];

export function Footer() {
  return (
    <footer className="border-t border-border/60 bg-slate-deep">
      <div className="container mx-auto max-w-7xl px-4 py-16">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gold/30 bg-card/40 p-5">
            <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-gold">
              Desk Access
            </h2>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li><Link to="/signals" className="hover:text-gold">SMC Execution Setups</Link></li>
              <li><Link to="/track-record" className="hover:text-gold">Performance Record</Link></li>
              <li><Link to="/case-studies" className="hover:text-gold">Case Studies</Link></li>
            </ul>
            <Link
              to="/pricing"
              className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-gradient-gold px-3 py-2 text-sm font-semibold text-gold-foreground transition-opacity hover:opacity-90"
            >
              View Membership
            </Link>
            <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-wider text-muted-foreground/80">
              Monthly and annual plans
            </p>
          </div>
          {columns.map((column) => (
            <div key={column.title}>
              <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-foreground">
                {column.title}
              </h2>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {column.links.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="hover:text-gold">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 grid gap-10 border-t border-border/60 pt-10 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2">
              <BrandMark />
              <span className="font-display text-lg font-bold">
                XAU <span className="text-gradient-gold">Pro</span> Academy
              </span>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Educational and quantitative research platform for XAUUSD market
              structure.
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
              <li>
                <a href="mailto:support@xauproacademy.com" className="hover:text-gold">support@xauproacademy.com</a>
              </li>
              <li>Support: Monday to Friday</li>
              <li>
                <Link to="/faq" className="hover:text-gold">Frequently Asked Questions</Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-gold">Contact Desk</Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-gold">About the Desk</Link>
              </li>
              <li>
                <Link to="/refund-policy" className="hover:text-gold">Cancellation &amp; Refunds</Link>
              </li>
            </ul>
            <div className="mt-5">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground/80">Follow the desk</p>
              <div className="mt-2 flex flex-wrap gap-2">
              <a
                href="https://x.com/XAUProAcademy"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="XAU Pro Academy on X"
                className="inline-flex items-center gap-2 rounded-md border border-border/60 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-gold/50 hover:text-gold"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5 fill-current">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                @XAUProAcademy
              </a>
              <a
                href="https://www.youtube.com/@XAUProAcademy"
                target="_blank"
                rel="me noopener noreferrer"
                aria-label="XAU Pro Academy on YouTube"
                className="inline-flex items-center gap-2 rounded-md border border-border/60 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-gold/50 hover:text-gold"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5 fill-current">
                  <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6z" />
                </svg>
                @XAUProAcademy
              </a>
              </div>
            </div>
          </div>

          <div className="space-y-3 text-xs leading-relaxed text-muted-foreground/80 lg:col-span-3">
            <p className="font-mono uppercase tracking-wider text-foreground/80">Risk and Compliance Notice</p>
            <p>
              <span className="text-foreground/90">Nature of Service:</span> XAU Pro Academy is an educational and
              quantitative research platform focused on XAUUSD market structure analysis. We are not a broker,
              investment adviser, or licensed financial institution. Nothing on this site constitutes financial,
              investment, or trading advice.
            </p>
            <p>
              <span className="text-foreground/90">Trading Risk:</span> Trading gold and leveraged instruments carries
              a high level of risk and may result in the loss of some or all invested capital. Past performance,
              including figures published in our Track Record and Case Studies, is not indicative of future results
              and does not guarantee similar outcomes. All trading decisions remain the sole responsibility of the user.
            </p>
            <p>
              <span className="text-foreground/90">Data and Accuracy:</span> Market data, price feeds, and analysis
              tools are provided for informational purposes only. We do not warrant the accuracy, completeness, or
              timeliness of any data displayed, and technical interruptions may occur.
            </p>
            <p>
              <span className="text-foreground/90">Third Parties:</span> We do not receive compensation from brokers,
              prop firms, or third parties referenced on this platform.
            </p>
            <p>
              <span className="text-foreground/90">Terms:</span> By using this site you agree to our{" "}
              <Link to="/terms" className="text-foreground/90 underline underline-offset-2">Terms of Service</Link>,{" "}
              <Link to="/privacy" className="text-foreground/90 underline underline-offset-2">Privacy Policy</Link>,{" "}
              <Link to="/refund-policy" className="text-foreground/90 underline underline-offset-2">Cancellation &amp; Refund Policy</Link>, and{" "}
              <Link to="/risk-disclaimer" className="text-foreground/90 underline underline-offset-2">Risk Disclaimer</Link>.
            </p>
          </div>
        </div>

        <div className="mt-6 pt-2 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} XAU Pro Academy™ / Powered by Orion AI™ Execution Engine / Proprietary systems of Orion AI Technologies. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
}
